// Renders one generated question: stem, options, and — once answered — the
// correct/incorrect marking plus the explanation and its provenance seal.
//
// Answering is persisted twice: the response is written onto the library item
// (recordResponse) and the aggregate progress counter is bumped (bumpProgress).
// Both are uid-scoped. A question already answered in a past session renders in
// its answered state on load, so the Library is a true review surface.

import { useState } from 'react';
import { recordResponse, bumpProgress, toggleSaved } from '../lib/db';
import { useAuth } from '../context/AuthContext';
import ProvenanceSeal from './ProvenanceSeal';

const KEYS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

export default function QuestionCard({ item, onChanged }) {
  const { user } = useAuth();
  const q = item.question;
  const [selectedId, setSelectedId] = useState(item.response?.selectedOptionId ?? null);
  const [saved, setSaved] = useState(item.saved);
  const [busy, setBusy] = useState(false);
  const answered = selectedId !== null;

  async function choose(opt) {
    if (answered || !user || busy) return;
    setBusy(true);
    setSelectedId(opt.id);
    try {
      await recordResponse(user.uid, item.itemId, opt);
      await bumpProgress(user.uid, item.examId, opt.correct);
      onChanged?.({
        ...item,
        response: {
          selectedOptionId: opt.id,
          correct: opt.correct,
          answeredAt: Date.now(),
        },
      });
    } finally {
      setBusy(false);
    }
  }

  async function onToggleSave() {
    if (!user || busy) return;
    setBusy(true);
    const next = !saved;
    setSaved(next);
    try {
      await toggleSaved(user.uid, item.itemId, next);
      onChanged?.({ ...item, saved: next });
    } finally {
      setBusy(false);
    }
  }

  const correctOpt = q.options.find((o) => o.correct);

  return (
    <div className="card">
      <div className="q-meta">
        <span className="badge">{q.contentArea}</span>
        <span className="badge">{q.mode.replace(/_/g, ' ')}</span>
        <span className="badge">{q.cognitiveLevel}</span>
        <span className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={onToggleSave} disabled={busy}>
          {saved ? '★ Saved' : '☆ Save'}
        </button>
      </div>

      <p className="q-stem">{q.stem}</p>

      <div className="options">
        {q.options.map((o, i) => {
          let cls = 'option';
          if (answered) {
            cls += ' disabled';
            if (o.correct) cls += ' correct';
            else if (o.id === selectedId) cls += ' incorrect';
          }
          return (
            <button
              key={o.id}
              className={cls}
              onClick={() => choose(o)}
              disabled={answered || busy}
            >
              <span className="key">{KEYS[i] ?? i + 1}</span>
              <span>{o.text}</span>
            </button>
          );
        })}
      </div>

      {answered && (
        <div className="explanation stack">
          <div>
            <h4>
              {selectedId === correctOpt?.id ? 'Correct' : 'Incorrect'} —{' '}
              {correctOpt ? `answer ${KEYS[q.options.indexOf(correctOpt)]}` : ''}
            </h4>
            <p className="muted" style={{ margin: 0 }}>
              {q.explanation.rationale}
            </p>
          </div>
          <ProvenanceSeal claim={q.explanation.sourceClaim} />
        </div>
      )}
    </div>
  );
}
